/* +----------------------------------------------------------------------+
 * | Angular 15                                                     |
 * +----------------------------------------------------------------------+
 * | Productor (c) 2021 - Infodec S.A.                                    |
 * +----------------------------------------------------------------------+
 * | Editor: Andres Moreno Montoya <andres.moreno@infodeclat.com> |
 * | 		                                                              |
 * +----------------------------------------------------------------------+
 * 
 *
 * Servicio que proporciona etiquetas de texto para la aplicación de pagos basado en el idioma y canal
 * 
 *
 * @package  Sistema
 * @version  1
 * @Editor   Andres Moreno Montoya <andres.moreno@infodeclat.com> 2026-01- 21
 * @since    Angular 15
 */
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
// Servicio que proporciona etiquetas de texto para la aplicación de pagos basado en el idioma y canal
export class LabelService {

  constructor() { }

  /**
   * Objeto que contiene todas las etiquetas de la aplicación
   * organizadas por idioma (español e inglés).
   */
  labels = {
    // Etiquetas en español e inglés
    es: {
      cardNumber: '*Número de tarjeta',
      expiration: '*Vigencia',
      cvv: '*CVV',
      email: '*Introduce tu correo electrónico',
      placeholder: {
        expiration: 'Expiración (mm/yy)',
        email: '*Introduce tu correo electrónico',
        cardNumber: 'Número de tarjeta',
        cvv: '***'
      },
      errors: {
        emailPattern: 'El formato del correo no es válido.',
        domainTooLong: 'El dominio no debe exceder los 50 caracteres.',
        subdomainTooLong: 'Un subdominio excede los 50 caracteres permitidos.',
        required: 'El campo es obligatorio.'
      },
      terms: 'Acepto',
      termsLink: 'Términos y condiciones',
      cardAffiliation: 'Afiliar mi tarjeta al Débito Automático Mensual',
      saveCard: 'Recordar los datos de mi tarjeta',
      continue: 'Continuar',

      //Textos de cofirmación de pago
      confirmation: 'Confirma tus datos',
      cardNumberCp: 'Número de tarjeta',
      expirationCp: 'Vencimiento',
      phone: 'Teléfono',
      changeData: 'Cambiar datos de tarjeta',
      errorProcess: 'Error en el proceso de pago',

      //Texto Comprobante de pago
      approvedMessage: 'Tu satisfacción nos impulsa a mejorar nuestros servicios. Te deseamos éxitos en el alcance y logro de tus metas. Recuerda que estamos para servirte.',
      approvedMessageEmail: 'Recibirás un correo electrónico de confirmación de pago en tu factura Claro.',
      reasonRejection: 'Motivo de rechazo',
      transactionDetail: 'Detalle de transacción',
      transaction: 'Transacción',
      amountPaid: 'Monto pagado',
      referenceTransation: 'Referencia transacción',
      authorizationCode: 'Código autorización',
      dateTime: 'Fecha y hora',
      messageDAM: 'Tu débito automático mensual no se puede afiliar, ya que solo se permiten tarjetas emitidas en {country} para este proceso.'
    },
    en: {
      cardNumber: 'Card Number',
      expiration: 'Expiration Date',
      cvv: 'Security Code',
      email: 'Enter your email address',
      placeholder: {
        expiration: 'mm/yy',
        email: 'example@email.com',
        cardNumber: 'Card Number',
        cvv: '***'
      },
      errors: {
        emailPattern: 'The email format is invalid.',
        domainTooLong: 'The domain should not exceed 50 characters.',
        subdomainTooLong: 'A subdomain exceeds the 50 allowed characters.',
        required: 'This field is required.'
      },
      terms: 'I accept',
      termsLink: 'Terms and Conditions',
      cardAffiliation: 'Link my card to Monthly Automatic Debit',
      saveCard: 'Remember my card information',
      continue: 'Continue',

      //Confirmation payment texts
      confirmation: 'Confirm your details',
      cardNumberCp: 'Card number',
      expirationCp: 'Expiration',
      phone: 'Phone',
      changeData: 'Change card details',
      errorProcess: 'Error in the payment process',

      //Texto Comprobante de pago
      approvedMessage: 'Your satisfaction motivates us to improve our services. We wish you success in reaching and achieving your goals. Remember, we are here to serve you.',
      approvedMessageEmail: 'You will receive a payment confirmation email at your Claro invoice.',
      reasonRejection: 'Reason for rejection',
      transactionDetail: 'Transaction Detail',
      amountPaid: 'Amount paid',
      referenceTransation: 'Reference Transation',
      authorizationCode: 'Authorization code',
      dateTime: 'Data and time',
      messageDAM: 'Your monthly automatic debit cannot be registered, as only cards issued in {country} are allowed for this process.'
    },


  };

  // Método para obtener la etiqueta correspondiente según la clave, idioma y canal
  getLabel(
    key: string,
    lang: 'es' | 'en',
    channel?: number
  ): string {


    // Divide la clave por niveles: errors.required -> ['errors','required']
    const keys = key.split('.');

    // Si es channel 7, retorna ambos idiomas separados por "/"
    if (channel === 7) {

      // excepciones para placeholders para que no muestren la division
      switch (key) {
        case 'placeholder.email':
        case 'placeholder.expiration':
        case 'placeholder.cvv':
          return this.resolveKey(this.labels.en, keys) || key;

        case 'approvedMessage':
        case 'approvedMessageEmail':
        case 'reasonRejection':
        case 'transactionDetail':
        case 'amountPaid':
        case 'referenceTransation':
        case 'authorizationCode':
        case 'dateTime':
        case 'messageDAM':
          return this.resolveKey(this.labels.es, keys) || key;
      }

      //Obtiene el valor en español
      const esValue = this.resolveKey(this.labels.es, keys);

      //Obtiene el valor en inglés
      const enValue = this.resolveKey(this.labels.en, keys);

      if (!esValue || !enValue) return key;
    
      // Retorna formato bilingüe
      return `${esValue} / ${enValue}`;
    }

    /**
     * Flujo normal:
     * retorna el textos en español
     */
    return this.resolveKey(this.labels[lang], keys) || key;
  }

  // Método para resolver la clave en el objeto de etiquetas de forma segura
  private resolveKey(obj: any, keys: string[]): string | null {
    let value = obj;
    for (const k of keys) {
      value = value?.[k];
      if (!value) return null;
    }
    //
    return value;
  }
}
